import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { RefreshCw, Inbox } from "lucide-react";
import axios from "axios";
import { Navigation } from "../components/Navigation";
import { API } from "../lib/api";

const FIELDS = [
  ["email", "Email"],
  ["phone", "Phone"],
  ["organization", "Organization"],
  ["inquiry_type", "Inquiry Type"],
  ["event_name", "Event / Project"],
  ["event_date", "Date"],
  ["event_location", "Location"],
  ["access_needs", "Access Needed"],
  ["budget", "Budget / Sponsorship"],
  ["deadline", "Deadline"],
  ["links", "Links / Press"],
];

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/inquiries`)
      .then(({ data }) => setInquiries(data))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  return (
    <div className="min-h-screen bg-ink grain">
      <Navigation />
      <header className="pt-32 pb-10 px-6 lg:px-10 border-b border-white/10">
        <div className="mx-auto max-w-6xl flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5">
          <div>
            <p className="font-body text-xs uppercase tracking-[0.28em] text-crimson mb-4">Admin</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-white leading-none">Coverage Inquiries</h1>
            <p className="mt-4 font-body text-white/50">
              {inquiries.length} {inquiries.length === 1 ? "inquiry" : "inquiries"} captured by Phusic's Assistant.
            </p>
          </div>
          <button
            data-testid="refresh-inquiries"
            onClick={load}
            className="self-start sm:self-auto shrink-0 inline-flex items-center gap-2 rounded-full border border-white/20 text-white/80 font-body text-xs uppercase tracking-[0.15em] px-4 py-2.5 hover:border-crimson hover:text-white transition-colors"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      <main className="px-6 lg:px-10 py-12">
        <div className="mx-auto max-w-6xl">
          {loading ? (
            <p className="font-body text-white/50">Loading…</p>
          ) : inquiries.length === 0 ? (
            <div data-testid="no-inquiries" className="flex flex-col items-center gap-4 py-24 text-white/40">
              <Inbox size={40} />
              <p className="font-body">No inquiries yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {inquiries.map((q, i) => (
                <motion.div
                  key={q.id}
                  data-testid={`inquiry-card-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  className="rounded-xl border border-white/10 bg-surface p-6"
                >
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="font-serif text-2xl text-white">{q.name || "Unknown"}</h3>
                      <p className="font-body text-xs text-white/40 mt-1">
                        {new Date(q.created_at).toLocaleString()}
                      </p>
                    </div>
                    {q.inquiry_type && (
                      <span className="shrink-0 font-body text-[10px] uppercase tracking-[0.18em] text-crimson bg-crimson/10 border border-crimson/30 px-2.5 py-1 rounded-full">
                        {q.inquiry_type}
                      </span>
                    )}
                  </div>

                  {q.details && (
                    <p className="font-body text-sm text-white/70 leading-relaxed mb-4 border-l-2 border-crimson/50 pl-3">
                      {q.details}
                    </p>
                  )}

                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                    {FIELDS.filter(([k]) => q[k]).map(([k, label]) => (
                      <div key={k} className="min-w-0">
                        <dt className="font-body text-[10px] uppercase tracking-[0.16em] text-white/35">{label}</dt>
                        <dd className="font-body text-sm text-white/80 break-words">{q[k]}</dd>
                      </div>
                    ))}
                  </dl>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
